import { siteConfig, getPartnerToken } from '@afrilink/config';

/**
 * UTM attribution params appended to portal-bound CTAs.
 *
 * Per Q3 (revised 2026-05-07, see memory/marketing_v2_architecture_decisions.md):
 * we do not run third-party analytics. Funnel attribution flows via UTM tags →
 * portal sign-up captures them into `customer_link.acquisition_source`.
 *
 * - source: high-level origin. We default to 'marketing' for any link
 *   from this site so admins can split direct vs marketing-attributed signups.
 * - medium: WHERE the click happened. Common values: 'hero' | 'header' |
 *   'plan-card' | 'cta' | 'footer'.
 * - campaign: WHICH offering the click was about. Common: 'hosting' |
 *   'domains' | 'fibre' | 'general'.
 * - content: the specific item — usually a plan slug like 'basic-monthly'
 *   or a TLD like '.co.za'.
 */
export interface UtmParams {
  source?: string;
  medium?: 'hero' | 'header' | 'plan-card' | 'cta' | 'footer' | string;
  campaign?: 'hosting' | 'domains' | 'fibre' | 'general' | string;
  content?: string;
}

/**
 * Resolve a fully-qualified portal URL for a given path (defaults to sign-in).
 *
 * Dev:  http://localhost:5174 + path  (matches `pnpm dev:portal` Vite port)
 * Prod: https://portal.sighthost.online + path
 *
 * Override the resolved base via `VITE_PORTAL_URL` for staging / preview deploys.
 *
 * Optional `utm` param appends utm_source / utm_medium / utm_campaign /
 * utm_content query string params for funnel attribution (M3.14). Defaults
 * source to 'marketing' if any UTM param is provided — every marketing-site
 * CTA is implicitly attributed to marketing.
 */
export function getPortalUrl(
  path: string = siteConfig.portal.signInPath,
  utm?: UtmParams,
  query?: Record<string, string>,
): string {
  // Use `||` (not `??`) so empty-string overrides — which Vite/Vitest
  // sometimes sets for unset VITE_* vars — fall through to the defaults.
  // The new URL() constructor below requires a valid absolute URL with scheme.
  const override = import.meta.env.VITE_PORTAL_URL as string | undefined;
  const base =
    override ||
    (import.meta.env.DEV ? siteConfig.portal.devUrl : siteConfig.portal.productionUrl);
  const url = new URL(`${base.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`);

  if (utm) {
    url.searchParams.set('utm_source', utm.source ?? 'marketing');
    if (utm.medium) url.searchParams.set('utm_medium', utm.medium);
    if (utm.campaign) url.searchParams.set('utm_campaign', utm.campaign);
    if (utm.content) url.searchParams.set('utm_content', utm.content);
  }

  // Extra query params — used for the existing handshakes that coexist with
  // UTM (e.g. ?plan=basic-monthly tells /app/services/new which plan card was
  // clicked, distinct from utm_content which is for attribution reporting).
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      url.searchParams.set(key, value);
    }
  }

  // Partner acquisition token (L3). Injected HERE rather than at each call site
  // because every marketing CTA already funnels through this function — one
  // choke point, so a new CTA inherits attribution automatically and cannot
  // silently omit it. That matters: a CTA that forgets the token produces a
  // sale the partner is owed a share of but does not receive, and nothing
  // about the data looks wrong.
  //
  // `pt` is deliberately NOT a utm_* param. UTM carries customer-visible
  // marketing semantics and is routinely stripped, rewritten or reordered by
  // link shorteners and mail clients. A tag that decides a long-running payment
  // obligation does not belong in a field the ecosystem treats as disposable
  // (architect ruling AR-8).
  const partnerToken = getPartnerToken();
  if (partnerToken) url.searchParams.set('pt', partnerToken);

  return url.toString();
}
